/**
 * App shell (DESIGN.md §9.1): TopBar · NavRail · Content · optional DetailPanel, shared by
 * webmail and admin. App code only supplies content, the localized navigation registry and
 * a few slots; it never composes the shell regions itself (§13 rule 7).
 *
 * Responsive (§9.1 table): ≥ xl rail expanded (collapsible), md–xl icon rail, < md rail in a
 * Sheet opened from the TopBar.
 */
import { ChevronDownIcon, MailIcon, MenuIcon, PanelLeftCloseIcon, PanelLeftOpenIcon, SearchIcon } from 'lucide-react';
import { createContext, useContext, useState, type ComponentProps, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { PaletteActionsProvider, useRegisteredPaletteActions } from '../actions/palette';
import { ActionRunnerProvider } from '../actions/runner';
import { Button } from '../components/button';
import { Kbd } from '../components/misc';
import { Sheet, SheetContent, SheetTitle } from '../components/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/tooltip';
import { BREAKPOINTS, useMediaQuery } from '../hooks/use-media-query';
import { useShortcut } from '../hooks/use-shortcut';
import { cn } from '../lib/utils';
import { findNavItem, type NavItem, type NavRegistry } from '../navigation';
import { useUiStrings } from '../strings';
import { CommandPalette, type PaletteAction } from './command-palette';
import { LinkProvider, ShellLink, type LinkComponent } from './link';
import { PaneResizeHandle, usePaneWidth } from './resizable-pane';

export interface AppShellProps {
  productName: string;
  /** Logo element; defaults to a neutral mail icon until branding exists. */
  logo?: ReactNode;
  /** Localized navigation registry (already filtered by permission). */
  navigation: NavRegistry;
  /** Compact module icons; grouped navigation remains the default. */
  navigationVariant?: 'groups' | 'icons';
  /** Bottom module bar stays outside the folder rail's scroll owner. */
  navigationPlacement?: 'top' | 'bottom';
  /** Current path relative to the app base (drives the active item). */
  activePath: string;
  /** Router link adapter. */
  linkComponent: LinkComponent;
  /** Called by the command palette to navigate. */
  onNavigate: (path: string) => void;
  /** Optional guarded navigation for ordinary rail links; modified clicks keep native link behavior. */
  onLinkNavigate?: (path: string) => void;
  /**
   * The app's main "create" action (webmail: New message). On phones (< md) it is a floating
   * action button, because the rail that holds it on larger screens is hidden in a Sheet. It is
   * also listed in the command palette.
   */
  primaryAction?: PaletteAction;
  /** Keeps the desktop create action reachable when the rail is collapsed. */
  showPrimaryActionInRail?: boolean;
  /** Extra command palette entries ("Create user", …). Pages add selection actions with `usePaletteActions`. */
  actions?: readonly PaletteAction[];
  /** Replaces the default search field (e.g. webmail's mail search). */
  search?: ReactNode;
  /** The custom `search` takes Ctrl K itself; the palette then opens with its alternative chord only. */
  searchOwnsCommandShortcut?: boolean;
  /** Items at the right end of the TopBar before the user menu (display menu, notifications). */
  topBarEnd?: ReactNode;
  userMenu: ReactNode;
  /** App-specific content under the nav groups when the rail is expanded (e.g. folder tree). */
  railContent?: ReactNode;
  /** Allow bounded pointer/keyboard resizing of the expanded desktop folder rail. */
  resizableRail?: boolean;
  /**
   * Makes the rail sections collapsible with memory: only the active section is open by default and
   * the user's choices are stored under this key (include the user, so the state is per user).
   */
  rememberSections?: string;
  children: ReactNode;
}

export function AppShell(props: AppShellProps) {
  const { productName, logo, navigation, navigationVariant = 'groups', navigationPlacement = 'top', activePath, linkComponent, onNavigate, onLinkNavigate, primaryAction, showPrimaryActionInRail = false, actions = [], search, searchOwnsCommandShortcut = false, topBarEnd, userMenu, railContent, resizableRail = false, rememberSections, children } =
    props;
  const paletteActions = primaryAction ? [primaryAction, ...actions] : actions;
  const PrimaryIcon = primaryAction?.icon;
  const strings = useUiStrings();
  const isDesktop = useMediaQuery(BREAKPOINTS.md);
  const isWide = useMediaQuery(BREAKPOINTS.xl);
  const [collapsedPreference, setCollapsedPreference] = useState<boolean | null>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [panelHost, setPanelHost] = useState<HTMLElement | null>(null);
  const collapsed = collapsedPreference ?? !isWide;
  const railPane = usePaneWidth('platform.navigationWidth', 256, 208, 400);

  useShortcut('app.commandPalette', () => setPaletteOpen(true), { enabled: !searchOwnsCommandShortcut });
  useShortcut('app.commandPaletteAlt', () => setPaletteOpen(true));

  const toggleNav = () => (isDesktop ? setCollapsedPreference(!collapsed) : setMobileNavOpen(true));
  const navLabel = !isDesktop ? strings.openNavigation : collapsed ? strings.expandNavigation : strings.collapseNavigation;
  const NavToggleIcon = !isDesktop ? MenuIcon : collapsed ? PanelLeftOpenIcon : PanelLeftCloseIcon;

  return (
    <PanelHostContext.Provider value={panelHost}>
    <ActionRunnerProvider>
    <PaletteActionsProvider>
    <LinkProvider component={linkComponent}>
      <div className="flex h-dvh flex-col bg-background text-foreground">
        <a
          href="#content"
          className="sr-only z-50 rounded-md bg-primary px-3 py-2 text-primary-foreground focus:not-sr-only focus:absolute focus:top-2 focus:left-2"
        >
          {strings.skipToContent}
        </a>

        <header className="flex h-bar shrink-0 items-center gap-2 border-b bg-background px-2">
          <Button variant="ghost" size="icon" aria-label={navLabel} onClick={toggleNav}>
            <NavToggleIcon />
          </Button>
          <div className="flex min-w-0 items-center gap-2 pr-2">
            <span className="text-primary [&_svg]:size-5">{logo ?? <MailIcon />}</span>
            <span className="truncate text-sm font-semibold">{productName}</span>
          </div>
          <div className="flex min-w-0 flex-1 justify-center [&>div]:w-full [&>div]:max-w-md [&>input]:w-full [&>input]:max-w-md">
            {search ?? (
              <Button
                variant="outline"
                className="w-full max-w-md justify-start text-muted-foreground"
                onClick={() => setPaletteOpen(true)}
              >
                <SearchIcon />
                <span className="flex-1 truncate text-left">{strings.search}</span>
                <Kbd className="hidden sm:inline-flex">{strings.commandShortcut}</Kbd>
              </Button>
            )}
          </div>
          <div className="flex items-center gap-1">
            {topBarEnd}
            {userMenu}
          </div>
        </header>

        <div className="flex min-h-0 flex-1">
          {isDesktop ? (
            <aside
              style={resizableRail && !collapsed ? { width: railPane.width } : undefined}
              aria-label={strings.mainNavigation}
              className={cn(
                'flex shrink-0 flex-col border-r bg-sidebar text-sidebar-foreground transition-all duration-200 ease-out',
                navigationPlacement === 'bottom' ? 'overflow-hidden' : 'overflow-y-auto',
                collapsed ? 'w-rail-collapsed' : 'w-rail',
              )}
            >
              {navigationPlacement === 'top' && <NavRail navigation={navigation} activePath={activePath} collapsed={collapsed} variant={navigationVariant} sectionMemory={rememberSections} onLinkNavigate={onLinkNavigate} />}
              <div className={navigationPlacement === 'bottom' ? 'min-h-0 flex-1 overflow-y-auto' : 'contents'}>
              {showPrimaryActionInRail && primaryAction && (
                <div className="p-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        className="w-full"
                        size={collapsed ? 'icon' : 'md'}
                        aria-label={primaryAction.label}
                        onClick={primaryAction.onSelect}
                      >
                        {PrimaryIcon && <PrimaryIcon aria-hidden />}
                        {!collapsed && primaryAction.label}
                      </Button>
                    </TooltipTrigger>
                    {collapsed && <TooltipContent side="right">{primaryAction.label}</TooltipContent>}
                  </Tooltip>
                </div>
              )}
              {!collapsed && railContent}
              </div>
              {navigationPlacement === 'bottom' && <NavRail navigation={navigation} activePath={activePath} collapsed={collapsed} variant={navigationVariant} sectionMemory={rememberSections} onLinkNavigate={onLinkNavigate} placement="bottom" />}
            </aside>
          ) : (
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetContent side="left" className="w-rail bg-sidebar p-0 text-sidebar-foreground">
                <SheetTitle className="sr-only">{strings.mainNavigation}</SheetTitle>
                <div className={cn('flex h-full flex-col pt-10', navigationPlacement === 'bottom' ? 'overflow-hidden' : 'overflow-y-auto')}>
                  {navigationPlacement === 'top' && <NavRail navigation={navigation} activePath={activePath} collapsed={false} variant={navigationVariant} sectionMemory={rememberSections} onLinkNavigate={onLinkNavigate} onNavigate={() => setMobileNavOpen(false)} />}
                  <div className={navigationPlacement === 'bottom' ? 'min-h-0 flex-1 overflow-y-auto' : 'contents'}>{railContent}</div>
                  {navigationPlacement === 'bottom' && <NavRail navigation={navigation} activePath={activePath} collapsed={false} variant={navigationVariant} sectionMemory={rememberSections} onLinkNavigate={onLinkNavigate} onNavigate={() => setMobileNavOpen(false)} placement="bottom" />}
                </div>
              </SheetContent>
            </Sheet>
          )}

          {isDesktop && !collapsed && resizableRail && <PaneResizeHandle pane={railPane} label={strings.mainNavigation} />}
          <main id="content" tabIndex={-1} className="relative min-w-0 flex-1 overflow-auto outline-none">
            {children}
          </main>

          <div ref={setPanelHost} className="contents" />
        </div>

        {primaryAction && (!isDesktop || !showPrimaryActionInRail) && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="fab" aria-label={primaryAction.label} className="fixed right-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-30 w-12 rounded-full px-0 shadow-lg" onClick={primaryAction.onSelect}>
                {PrimaryIcon && <PrimaryIcon aria-hidden className="size-5" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">{primaryAction.label}</TooltipContent>
          </Tooltip>
        )}

        <ShellCommandPalette
          open={paletteOpen}
          onOpenChange={setPaletteOpen}
          navigation={navigation}
          actions={paletteActions}
          onNavigate={onNavigate}
        />
      </div>
    </LinkProvider>
    </PaletteActionsProvider>
    </ActionRunnerProvider>
    </PanelHostContext.Provider>
  );
}

/** The palette with the shell's static actions plus the ones pages register (selection actions). */
function ShellCommandPalette({ actions = [], ...props }: ComponentProps<typeof CommandPalette>) {
  const registered = useRegisteredPaletteActions();
  return <CommandPalette {...props} actions={[...actions, ...registered]} />;
}

const PanelHostContext = createContext<HTMLElement | null>(null);

/**
 * Places its children (a `DetailPanel`) in the shell's detail region, to the right of the
 * content. Pages render it conditionally: `{selected && <ShellDetail><DetailPanel …/></ShellDetail>}`.
 */
export function ShellDetail({ children }: { children: ReactNode }) {
  const host = useContext(PanelHostContext);
  return host ? createPortal(children, host) : null;
}

interface NavRailProps {
  navigation: NavRegistry;
  activePath: string;
  collapsed: boolean;
  variant?: 'groups' | 'icons';
  placement?: 'top' | 'bottom';
  onNavigate?: () => void;
  onLinkNavigate?: ((path: string) => void) | undefined;
  /** Storage key: sections start collapsed except the active one and the user's choices are remembered. */
  sectionMemory?: string | undefined;
}

/** Remembered rail sections: the sections the user opened besides the active one. */
interface SectionChoices {
  open: string[];
}

function readChoices(key: string | undefined): SectionChoices {
  if (!key) return { open: [] };
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) ?? 'null') as Partial<SectionChoices> | null;
    return { open: Array.isArray(parsed?.open) ? parsed.open.filter((id) => typeof id === 'string') : [] };
  } catch {
    return { open: [] };
  }
}

/** Grouped navigation links (DESIGN.md §9.1 NavRail). Collapsed = icons with tooltips. */
export function NavRail({ navigation, activePath, collapsed, variant = 'groups', placement = 'top', onNavigate, onLinkNavigate, sectionMemory }: NavRailProps) {
  const active = findNavItem(navigation, activePath);
  const activeId = active?.item.id;
  const activeGroupId = active?.group.id;
  const [closedGroups, setClosedGroups] = useState<ReadonlySet<string>>(new Set());
  const [choices, setChoices] = useState<SectionChoices>(() => readChoices(sectionMemory));
  const [memoryKey, setMemoryKey] = useState(sectionMemory);
  if (memoryKey !== sectionMemory) {
    setMemoryKey(sectionMemory);
    setChoices(readChoices(sectionMemory));
  }
  // The active page's section is always expanded: on every mount (desktop rail, phone sheet) and every
  // navigation, including redirects of retired URLs. Collapsing it only lasts until the next navigation
  // and is never stored, so a remembered choice can never hide the page the user is on.
  const [activeCollapsed, setActiveCollapsed] = useState(false);
  const [seenPath, setSeenPath] = useState(activePath);
  if (seenPath !== activePath) {
    setSeenPath(activePath);
    setActiveCollapsed(false);
  }
  const remembered = sectionMemory !== undefined;
  const isOpen = (id: string) => (remembered ? (id === activeGroupId ? !activeCollapsed : choices.open.includes(id)) : !closedGroups.has(id));

  const toggleGroup = (id: string) => {
    if (remembered) {
      if (id === activeGroupId) {
        setActiveCollapsed(!activeCollapsed);
        return;
      }
      const next: SectionChoices = { open: isOpen(id) ? choices.open.filter((entry) => entry !== id) : [...choices.open, id] };
      setChoices(next);
      try { window.localStorage.setItem(sectionMemory, JSON.stringify(next)); } catch { /* storage unavailable: the choice only lasts for this view */ }
      return;
    }
    setClosedGroups((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  };

  if (variant === 'icons') {
    return (
      <nav className={cn('flex shrink-0 gap-1 border-sidebar-border p-2', placement === 'bottom' ? 'border-t' : 'border-b', collapsed ? 'flex-col' : 'flex-wrap')}>
        {navigation.flatMap((group) => group.items).map((item) => (
          <NavRailLink key={item.id} item={item} active={item.id === activeId} collapsed onNavigate={onNavigate} onLinkNavigate={onLinkNavigate} tooltipSide={collapsed ? 'right' : placement === 'bottom' ? 'top' : 'bottom'} />
        ))}
      </nav>
    );
  }

  return (
    <nav className="flex flex-col gap-0.5 p-2">
      {navigation.map((group) => {
        const open = collapsed || group.flat || isOpen(group.id);
        return (
          <div key={group.id} className="flex flex-col gap-0.5">
            {group.flat ? null : collapsed ? (
              <div className="mx-2 my-1 h-px bg-sidebar-border first:hidden" />
            ) : (
              <button
                type="button"
                aria-expanded={open}
                onClick={() => toggleGroup(group.id)}
                className="flex h-control-sm items-center justify-between rounded-md px-2 text-xs font-medium text-muted-foreground outline-none hover:text-sidebar-foreground focus-visible:ring-3 focus-visible:ring-sidebar-ring/50"
              >
                {group.label}
                <ChevronDownIcon className={cn('size-3.5 transition-transform', !open && '-rotate-90')} />
              </button>
            )}
            {open &&
              group.items.map((item) => (
                <NavRailLink key={item.id} item={item} active={item.id === activeId} collapsed={collapsed} onNavigate={onNavigate} onLinkNavigate={onLinkNavigate} />
              ))}
          </div>
        );
      })}
    </nav>
  );
}

function NavRailLink({ item, active, collapsed, onNavigate, onLinkNavigate, tooltipSide = 'right' }: { item: NavItem; active: boolean; collapsed: boolean; onNavigate?: (() => void) | undefined; onLinkNavigate?: ((path: string) => void) | undefined; tooltipSide?: 'right' | 'bottom' | 'top' }) {
  const Icon = item.icon;
  const link = (
    <ShellLink
      href={item.path}
      aria-current={active ? 'page' : undefined}
      aria-label={collapsed ? item.label : undefined}
      onClick={(event) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (onLinkNavigate) { event.preventDefault(); onLinkNavigate(item.path); }
        onNavigate?.();
      }}
      className={cn(
        'relative flex h-control items-center gap-2 rounded-md px-2 text-sm outline-none transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-3 focus-visible:ring-sidebar-ring/50 [&_svg]:size-4 [&_svg]:shrink-0',
        collapsed && 'min-w-8 justify-center px-0',
        item.status === 'planned' && !active && 'text-muted-foreground',
        active &&
          'bg-sidebar-primary/10 font-medium text-sidebar-primary before:absolute before:inset-y-1.5 before:left-0 before:w-0.5 before:rounded-full before:bg-sidebar-primary',
      )}
    >
      <Icon />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </ShellLink>
  );
  if (!collapsed) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="flex flex-col">{link}</span>
      </TooltipTrigger>
      <TooltipContent side={tooltipSide}>{item.label}</TooltipContent>
    </Tooltip>
  );
}
